import { RequestService } from 'Utils/Api';
import { paymentIframePostAndListen } from 'Utils/Payment';
import { logger } from 'Utils/Logger';
import evergentCodeDetails from 'Config/EvergentCodeDetails.json';
import { getDefaultConfig } from 'Utils/Store';

// payment iframe events, suffix with REQUEST and RESPONSE
const ADD_SUBSCRIPTION = 'ADD_SUBSCRIPTION';
const CHANGE_SERVICE = 'CHANGE_SERVICE';
const UPDATE_PAYMENT_INFO = 'UPDATE_PAYMENT_INFO';

// payment method related Evergent error codes
const PAYMENT_METHOD_ERROR_CODES = ['eV1129', 'eV1130', 'eV1131', 'eV1132', 'eV1189', 'eV1385'];

const EvergentApi = (url, data) => {
  const { evergentApi } = getDefaultConfig();
  return RequestService.send({
    url: `${evergentApi}${url}`,
    method: 'POST',
    data,
    authenticate: true,
  })
    .then((res) => res.data)
    .catch((error) => logger.apiError('evergent', data, error));
};

// API helpers, also used on Api/Paypal.js
export const getPromoCode = ({ hasApplied, isValid, couponCode }) => (hasApplied && isValid ? couponCode : '');

export const getShippingAddress = (plans, checkout) => {
  if (checkout.hasShippingAddress && plans.isPlanSelected && plans.selectedPlan.hasMerchandise) {
    return checkout.shippingAddress;
  }
  return undefined;
};
const getSubscriptionServices = (plans) => {
  let service = [];
  if (plans.isPlanSelected && plans.selectedPlan) {
    const { selectedPlan } = plans;
    service = [
      {
        promotionId: selectedPlan.promotionID,
        serviceType: 'PACKAGE',
        serviceId: selectedPlan.planId,
        quantity: '1',
      },
    ];
  }
  return { service };
};
const transformParameters = (obj) => {
  return Object.entries(obj).reduce((acc, [paramName, paramValue]) => {
    return acc.concat([{ paramName, paramValue }]);
  }, []);
};
export const getSubscriptionParameters = ({ changeType = '', autoCouponCode = false }) => {
  const parametersObj = {};
  if (changeType) {
    parametersObj['Operation Type'] = changeType; // values: "Downgrade", "Upgrade";
  }
  if (autoCouponCode) {
    parametersObj.couponCodeEntry = 'auto';
  }
  const subscriptionParams = transformParameters(parametersObj);

  // all parameters
  return subscriptionParams;
};
const getAddSubscriptionOperationType = ({ subscription }) => {
  return subscription.isChurnedRestartAvailable ? 'Restartable' : undefined;
};
export const getChangeSubscriptionType = ({ subscription, selectedPlan }) => {
  // changeTypes: "Downgrade", "Upgrade"
  const { activeSubscriptionTier } = subscription;
  const { tier } = selectedPlan;
  if (tier < activeSubscriptionTier) {
    return 'Downgrade';
  }
  if (tier > activeSubscriptionTier) {
    return 'Upgrade';
  }
  return undefined;
};

export const isDowngradeSubscription = (subscription, selectedPlan) =>
  getChangeSubscriptionType({ subscription, selectedPlan }) === 'Downgrade';

export const isUpgradeSubscription = (subscription, selectedPlan) =>
  getChangeSubscriptionType({ subscription, selectedPlan }) === 'Upgrade';

export const getErrorMessageFromCode = (code) =>
  code ? evergentCodeDetails[code] || evergentCodeDetails.defaultErrorMessage : null;

const getPaymentMethodErrorCode = (code) => PAYMENT_METHOD_ERROR_CODES.includes(code);

const getAddSubscriptionRequest = (data) => {
  const { evergentApiKey } = getDefaultConfig();
  const {
    plans,
    promoCode,
    checkout,
    user,
    paymentmethodInfo = {},
    isSkipDefaultPromo,
    subscription,
    transactionReferenceMsg,
  } = data;
  const shippingAddress = getShippingAddress(plans, checkout);
  const services = getSubscriptionServices(plans);
  const parameters = getSubscriptionParameters({ autoCouponCode: !!promoCode.emailCoupon });
  const operationType = getAddSubscriptionOperationType({ subscription });
  const couponCode = getPromoCode(promoCode);
  const paymentMethodInfoData = {
    ...paymentmethodInfo,
    transactionReferenceMsg,
  };
  return {
    accessToken: user.accessToken,
    AddSubscriptionRequestMessage: {
      apiKey: evergentApiKey,
      channelPartnerID: 'FOXUSA',
      cpCustomerID: user.profileId,
      isSkipDefaultPromo,
      couponCode,
      parameters,
      services,
      paymentmethodInfo: paymentMethodInfoData,
      shippingAddress,
      operationType,
    },
  };
};

const getChangeSubscriptionRequest = (data) => {
  const { evergentApiKey } = getDefaultConfig();
  const { plans, subscription, promoCode, checkout, user, paymentmethodInfo = {} } = data;

  const { selectedPlan } = plans;
  const changeType = getChangeSubscriptionType({ subscription, selectedPlan });
  const shippingAddress = getShippingAddress(plans, checkout);
  const parameters = getSubscriptionParameters({ changeType, autoCouponCode: !!promoCode.emailCoupon });
  const couponCode = getPromoCode(promoCode);

  return {
    ChangeServiceRequestMessage: {
      changeType,
      apiKey: evergentApiKey,
      channelPartnerID: 'FOXUSA',
      cpCustomerID: user.profileId,
      couponCode,
      promotionId: selectedPlan.promotionID,
      newServiceID: selectedPlan.planId,
      paymentmethodInfo,
      parameters,
      shippingAddress,
    },
  };
};

const addSubscriptionResponse = (data) => {
  logger.apiResponse('add subscription', data);
  try {
    const addSubscriptionsResponse = data && data.addSubscriptionsResponseMessage;
    if (!addSubscriptionsResponse) {
      throw Error('Invalid API response: addSubscriptions');
    } else {
      // only addSubscription returns orderID, changeService and paypal api's return orderId (lowercase d)
      const { responseCode, orderID = '', failureMessage: { errorCode = null } = {} } = addSubscriptionsResponse;
      const errorMessage = getErrorMessageFromCode(errorCode);
      return { isSuccess: responseCode === '1', orderID, errorMessage, errorCode };
    }
  } catch (error) {
    logger.apiError('add subscription', data, error);
    return {
      errorCode: 'EV_API_ERROR',
      errorMessage: error.message,
      isSuccess: false,
      orderID: '',
    };
  }
};

const changeSubscriptionResponse = (data) => {
  logger.apiResponse('change service', data);
  try {
    const changeServiceResponse = data && data.ChangeServiceResponseMessage;
    if (!changeServiceResponse) {
      throw Error('Invalid API response: changeService');
    } else {
      const { responseCode, orderId = '', failureMessage: { errorCode = null } = {} } = changeServiceResponse;
      const errorMessage = getErrorMessageFromCode(errorCode);
      return { isSuccess: responseCode === '1', orderID: orderId, errorMessage, errorCode };
    }
  } catch (error) {
    logger.apiError('change service', data, error);
    return {
      errorCode: 'EV_API_ERROR',
      errorMessage: error.message,
      isSuccess: false,
      orderID: '',
    };
  }
};

// saved payments, can be used with card, paypal and applepay saved payment methods on Evergent profile.
export const savedPaymentAddSubscription = (data) => {
  const payload = getAddSubscriptionRequest(data);
  logger.apiRequest('add subscription', payload);
  return EvergentApi('/addSubscriptions', payload).then(addSubscriptionResponse);
};
export const savedPaymentChangeSubscription = (data) => {
  const payload = getChangeSubscriptionRequest(data);
  logger.apiRequest('change service', payload);
  return EvergentApi('/changeService', payload).then(changeSubscriptionResponse);
};

/* iframe post messages - start */
// new card iframe events, add and change subscription
export const cardPaymentAddSubscription = (data) => {
  const payload = getAddSubscriptionRequest(data);
  logger.apiRequest('add subscription iframe', payload);
  return paymentIframePostAndListen(ADD_SUBSCRIPTION, payload).then(addSubscriptionResponse);
};

export const cardPaymentChangeSubscription = (data) => {
  const payload = getChangeSubscriptionRequest(data);
  logger.apiRequest('change service iframe', payload);
  return paymentIframePostAndListen(CHANGE_SERVICE, payload).then(changeSubscriptionResponse);
};

export const couponPaymentAddSubscription = (data) => {
  const transactionReferenceMsg = {
    txMsg: 'Success',
    amount: '0.00',
    txRefNo: '',
    txtID: '',
  };
  const payload = getAddSubscriptionRequest({ ...data, transactionReferenceMsg });
  logger.apiRequest('add subscription coupon', payload);
  return EvergentApi('/addSubscriptions', payload).then(addSubscriptionResponse);
};

// edit card payment on profile page.
const updateCardPaymentMethod = () => paymentIframePostAndListen(UPDATE_PAYMENT_INFO, {});
/* iframe post messages - end */

// API
export const updateCardPayment = async () => {
  try {
    logger.apiRequest('update payment iframe', null);
    const response = await updateCardPaymentMethod();
    logger.apiResponse('update payment iframe', response);

    const {
      UpdatePaymentInfoResponseMessage: { responseCode = '0', failureMessage: { errorCode } = {} },
    } = response;

    if (!responseCode) {
      throw new Error('Invalid API response: updateCardPayment');
    } else {
      const isSuccess = responseCode === '1';
      const errorMessage = getErrorMessageFromCode(errorCode);
      return { isSuccess, errorMessage };
    }
  } catch (error) {
    logger.apiError('update payment iframe', null, error);
    return {
      errorCode: 'EV_API_ERROR',
      errorMessage: error.message,
      isSuccess: false,
    };
  }
};

export const applyPromo = async (couponCode) => {
  const { evergentApiKey } = getDefaultConfig();
  logger.apiRequest('apply coupon', couponCode);
  const payload = {
    ApplyCouponCodeRequestMessage: {
      channelPartnerID: 'FOXUSA',
      transactionType: 'retention',
      apiKey: evergentApiKey,
      couponCode,
    },
  };
  try {
    const response = await EvergentApi('/applyCouponV2', payload);
    const { ApplyCouponCodeResponseMessage } = response;
    const { responseCode = '0', failureMessage = {}, couponCodeDetails = [] } = ApplyCouponCodeResponseMessage;

    logger.apiResponse('apply coupon', { responseCode, failureMessage });

    if (responseCode === '0' && failureMessage) {
      const { errorCode } = failureMessage;
      const errorMessage = getErrorMessageFromCode(errorCode);
      const isPaymentMethodError = getPaymentMethodErrorCode(errorCode);
      return { isSuccess: responseCode === '1', errorMessage, isPaymentMethodError };
    }
    return { isSuccess: responseCode === '1', couponCodeDetails };
  } catch (error) {
    logger.apiError('apply coupon', { isSuccess: false, errorMessage: error.message }, error);
    return { isSuccess: false, errorMessage: error.message, errorCode: '' };
  }
};
